import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import Image from 'components/Basic/Image/Image';
import { ConfiguratorProductInDrawerActions } from 'components/Pages/Configurator/ConfiguratorProductInDrawerActions';
import useTranslation from 'next-translate/useTranslation';
import { useRef, useState } from 'react';
import { usePersistStore } from 'store/usePersistStore';
import { twJoin } from 'tailwind-merge';
import { ConfiguratorProductType } from 'types/configurator';

const ACTION_BUTTON_UNMOUNT_DELAY = 500;

type ConfiguratorProductInDrawerType = {
    product: ConfiguratorProductType;
};

export const ConfiguratorProductInDrawer: FC<ConfiguratorProductInDrawerType> = ({ product }) => {
    const { t } = useTranslation();
    const [isProductHovered, setIsProductHovered] = useState(false);
    const { actualCellWidthInPixels } = usePersistStore();
    const actionButtonsTimeout = useRef<NodeJS.Timeout | null>(null);

    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: product.id,
    });

    const getProductSizes = () => {
        return {
            width: product.width * actualCellWidthInPixels,
            height: product.height * actualCellWidthInPixels,
        };
    };

    const getImageSizes = () => {
        return {
            width: product.imageWidth * actualCellWidthInPixels,
            height: product.imageHeight * actualCellWidthInPixels,
        };
    };

    const shouldShowButtons = isProductHovered || isDragging;

    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            className={twJoin(
                'absolute left-0 top-0 transition-[filter]',
                shouldShowButtons && 'z-above brightness-105',
                isDragging && 'outline outline-4 outline-blueLight',
            )}
            style={{
                transform: CSS.Translate.toString(transform),
                top: product.coordinates.y * actualCellWidthInPixels,
                left: product.coordinates.x * actualCellWidthInPixels,
                width: `${getProductSizes().width}px`,
                height: `${getProductSizes().height}px`,
            }}
            onMouseEnter={() => {
                setIsProductHovered(true);
                if (actionButtonsTimeout.current) {
                    clearTimeout(actionButtonsTimeout.current);
                }
            }}
            onMouseLeave={() =>
                (actionButtonsTimeout.current = setTimeout(() => {
                    setIsProductHovered(false);
                }, ACTION_BUTTON_UNMOUNT_DELAY))
            }
        >
            <Image
                alt={`${t('Product image')} ${product.name}`}
                className="origin-top-left"
                height={getImageSizes().height}
                src={product.mainImage?.url ?? null}
                width={getImageSizes().width}
                style={{
                    transform:
                        product.alignment === 'horizontal'
                            ? `translateY(${(product.imageWidth / product.imageHeight) * 100}%) rotate(270deg)`
                            : 'rotate(0deg)',
                }}
            />
            <ConfiguratorProductInDrawerActions product={product} shouldShowButtons={shouldShowButtons} />
        </div>
    );
};
